import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api-client'
import {
  Mail, Save, TestTube2, CheckCircle2, XCircle,
  Eye, EyeOff, Loader2, Info, ExternalLink,
} from 'lucide-react'
import toast from 'react-hot-toast'

interface SmtpSettings {
  consumer_key: string
  consumer_secret: string
  from_email: string
  from_name: string
  api_endpoint: string
  is_active: boolean
  consumer_secret_set?: boolean
  updated_at?: string
}

const DEFAULT_ENDPOINT = 'https://api.eu.turbo-smtp.com/api/v2/mail/send'

const DEFAULT: SmtpSettings = {
  consumer_key: '', consumer_secret: '',
  from_email: '', from_name: '',
  api_endpoint: DEFAULT_ENDPOINT,
  is_active: true,
}

export default function SmtpSettingsPage() {
  const qc = useQueryClient()
  const [form, setForm] = useState<SmtpSettings>(DEFAULT)
  const [initialized, setInitialized] = useState(false)
  const [showSecret, setShowSecret] = useState(false)
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null)

  const { data: settingsData, isLoading } = useQuery({
    queryKey: ['smtp-settings'],
    queryFn: () => api.get<SmtpSettings>('/api/settings/smtp'),
  })

  useEffect(() => {
    if (settingsData && !initialized) {
      const d = settingsData.data
      setForm({
        consumer_key: d.consumer_key || '',
        consumer_secret: '',   // never returned from API
        from_email: d.from_email || '',
        from_name: d.from_name || '',
        api_endpoint: d.api_endpoint || DEFAULT_ENDPOINT,
        is_active: d.is_active ?? true,
        consumer_secret_set: d.consumer_secret_set,
      })
      setInitialized(true)
    }
  }, [settingsData])

  const saveMut = useMutation({
    mutationFn: () => api.put('/api/settings/smtp', form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['smtp-settings'] })
      toast.success('Configuração TurboSMTP guardada')
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail ?? 'Erro ao guardar'),
  })

  const testMut = useMutation({
    mutationFn: () => api.post<{ ok: boolean; message: string }>('/api/settings/smtp/test', {
      consumer_key: form.consumer_key,
      consumer_secret: form.consumer_secret || undefined,
      from_email: form.from_email,
      api_endpoint: form.api_endpoint || DEFAULT_ENDPOINT,
    }),
    onSuccess: (res: any) => setTestResult(res.data),
    onError: (e: any) => setTestResult({ ok: false, message: e?.response?.data?.detail ?? 'Erro ao testar' }),
  })

  function set(k: keyof SmtpSettings, v: any) {
    setForm(f => ({ ...f, [k]: v }))
    setTestResult(null)
  }

  if (isLoading) {
    return <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-primary-600" /></div>
  }

  return (
    <div className="max-w-2xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Mail className="h-6 w-6 text-primary-600" /> Configuração de Email (TurboSMTP)
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          Credenciais da API TurboSMTP para envio de notificações automáticas (tarefas, alertas, etc.)
        </p>
      </div>

      <div className="card space-y-5 p-6">

        {/* Active toggle */}
        <div className="flex items-center justify-between rounded-lg border border-gray-200 p-4">
          <div>
            <p className="font-medium text-gray-900">Serviço de email ativo</p>
            <p className="text-sm text-gray-500">Ativar ou desativar o envio de emails</p>
          </div>
          <button
            type="button"
            onClick={() => set('is_active', !form.is_active)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${form.is_active ? 'bg-primary-600' : 'bg-gray-300'}`}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${form.is_active ? 'translate-x-6' : 'translate-x-1'}`} />
          </button>
        </div>

        {/* Info box */}
        <div className="flex gap-2 rounded-lg bg-blue-50 p-3 text-sm text-blue-700">
          <Info className="h-4 w-4 mt-0.5 shrink-0" />
          <span>
            Obtenha as credenciais em{' '}
            <a
              href="https://dashboard.serversmtp.com"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold underline inline-flex items-center gap-1"
            >
              dashboard.serversmtp.com <ExternalLink className="h-3 w-3" />
            </a>
            {' '}→ <strong>API Keys</strong>.
            O Consumer Key e Consumer Secret são chaves de API — <em>não</em> são o email e palavra-passe da conta.
          </span>
        </div>

        {/* API credentials */}
        <div>
          <label className="label">Consumer Key</label>
          <input
            className="input w-full font-mono"
            placeholder="xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
            value={form.consumer_key}
            onChange={e => set('consumer_key', e.target.value)}
          />
        </div>

        <div>
          <label className="label">
            Consumer Secret
            {form.consumer_secret_set && !form.consumer_secret && (
              <span className="ml-2 text-xs text-green-600">(já definido)</span>
            )}
          </label>
          <div className="relative">
            <input
              className="input w-full pr-10 font-mono"
              type={showSecret ? 'text' : 'password'}
              placeholder={form.consumer_secret_set ? '••••••••••••••••' : 'Consumer Secret'}
              value={form.consumer_secret}
              onChange={e => set('consumer_secret', e.target.value)}
            />
            <button
              type="button"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
              onClick={() => setShowSecret(v => !v)}
            >
              {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        {/* From */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label">Email remetente</label>
            <input
              className="input w-full"
              placeholder="noreply@empresa.pt"
              value={form.from_email}
              onChange={e => set('from_email', e.target.value)}
            />
          </div>
          <div>
            <label className="label">Nome remetente</label>
            <input
              className="input w-full"
              placeholder="Shopfloor Hub"
              value={form.from_name}
              onChange={e => set('from_name', e.target.value)}
            />
          </div>
        </div>

        {/* API endpoint (advanced) */}
        <details className="group">
          <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700 select-none">
            Configurações avançadas
          </summary>
          <div className="mt-3">
            <label className="label">Endpoint API</label>
            <input
              className="input w-full font-mono text-xs"
              value={form.api_endpoint}
              onChange={e => set('api_endpoint', e.target.value)}
            />
            <p className="mt-1 text-xs text-gray-400">EU region: {DEFAULT_ENDPOINT}</p>
          </div>
        </details>

        {/* Test result */}
        {testResult && (
          <div className={`flex items-start gap-2 rounded-lg p-3 text-sm ${testResult.ok ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
            {testResult.ok
              ? <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
              : <XCircle className="h-4 w-4 mt-0.5 shrink-0" />}
            {testResult.message}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 pt-2">
          <button
            className="btn btn-secondary flex items-center gap-2"
            onClick={() => testMut.mutate()}
            disabled={!form.consumer_key || testMut.isPending}
          >
            {testMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <TestTube2 className="h-4 w-4" />}
            Testar ligação
          </button>
          <button
            className="btn btn-primary flex items-center gap-2"
            onClick={() => saveMut.mutate()}
            disabled={saveMut.isPending}
          >
            {saveMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Guardar
          </button>
        </div>
      </div>
    </div>
  )
}
